'use client';
import React from 'react';

const AboutSection: React.FC = () => {
  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const values = [
  {
    icon:
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2L3 7v6c0 6.55 4.67 12.68 11 14 6.33-1.32 11-7.45 11-14V7L14 2z" stroke="#387EF7" strokeWidth="2" strokeLinejoin="round" />
          <path d="M9 14l3 3 7-7" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Editorial Integrity',
    description: 'We uphold the highest standards of editorial quality, ensuring every publication reflects accuracy, clarity, and professional excellence.'
  },
  {
    icon:
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="22" height="22" rx="3" stroke="#387EF7" strokeWidth="2" />
          <path d="M8 9h12M8 13h12M8 17h8" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" />
        </svg>,

    title: 'Content Excellence',
    description: 'From concept to final publication, we are committed to producing content that informs, engages, and resonates with readers.'
  },
  {
    icon:
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="11" stroke="#387EF7" strokeWidth="2" />
          <path d="M14 8v6l4 2" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>,

    title: 'Timely Delivery',
    description: 'Periodical publishing demands precision and consistency. We are dedicated to meeting schedules and delivering on our commitments.'
  },
  {
    icon:
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 3C8 3 3 8 3 14s5 11 11 11 11-5 11-11S20 3 14 3z" stroke="#387EF7" strokeWidth="2" />
          <path d="M14 3c0 0-4 4-4 11s4 11 4 11M14 3c0 0 4 4 4 11s-4 11-4 11M3 14h22" stroke="#387EF7" strokeWidth="2" strokeLinecap="round" />
        </svg>,

    title: 'Digital Reach',
    description: 'We embrace modern publishing platforms to extend the reach of periodical content beyond print, connecting with audiences digitally.'
  }];


  return (
    <section id="about" className="py-24" style={{ backgroundColor: '#ffffff' }} aria-label="About Caps N Corks II">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block text-sm uppercase tracking-widest font-medium mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', color: '#0066CC', letterSpacing: '3px' }}>
            
            Who We Are
          </span>
          <h2
            className="mb-6"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(28px, 3.5vw, 42px)',
              color: '#122658',
              lineHeight: 1.2
            }}>
            
            About Caps N Corks II, LLC
          </h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: '#387EF7' }} />
          <p
            className="max-w-3xl mx-auto"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              color: '#7F7F90',
              lineHeight: 1.8
            }}>
            
            Caps N Corks II, LLC is a professional publishing company operating in the periodicals and magazine industry. Based in Jacksonville, Florida, we are focused on delivering quality editorial content and publishing services in the digital age.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left: Image */}
          <div className="relative">
            <div
              className="rounded-lg overflow-hidden"
              style={{ boxShadow: '4px 8px 30px rgba(18,38,88,0.15)' }}>
              
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_144077155-1787762422828.png"
                alt="Professional magazine publishing team reviewing editorial content and periodicals"
                className="w-full h-80 object-cover" />
              
            </div>
            {/* Accent box */}
            <div
              className="absolute -bottom-6 -right-6 w-32 h-32 rounded-lg z-0 hidden lg:block"
              style={{ backgroundColor: '#387EF7', opacity: 0.15 }} />
            
          </div>

          {/* Right: Content */}
          <div>
            <h3
              className="mb-6"
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: '26px',
                color: '#122658',
                lineHeight: 1.3
              }}>
              
              A Publishing Company Built on Quality and Professionalism
            </h3>
            <p
              className="mb-6"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '15px',
                color: '#7F7F90',
                lineHeight: 1.8
              }}>
              
              Caps N Corks II, LLC operates in the Periodicals sector, specifically in the Magazines: Publishing Only, Not Printed On Site category. Our focus is on the editorial and publishing side of the magazine industry — creating, curating, and distributing quality content.
            </p>
            <p
              className="mb-8"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '15px',
                color: '#7F7F90',
                lineHeight: 1.8
              }}>
              
              We are committed to serving our clients, partners, and readers with integrity, professionalism, and a dedication to the craft of publishing. Our Jacksonville, FL base allows us to serve clients locally and beyond.
            </p>
            <button
              onClick={() => handleScrollTo('contact')}
              className="inline-flex items-center justify-center px-8 py-3 text-white font-medium uppercase tracking-widest transition-all duration-200 hover:opacity-90"
              style={{
                backgroundColor: '#0066CC',
                borderRadius: '7px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                letterSpacing: '2px'
              }}>
              
              Get in Touch
            </button>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) =>
          <div
            key={index}
            className="p-6 rounded-lg transition-all duration-300 hover:-translate-y-1"
            style={{
              backgroundColor: '#ffffff',
              boxShadow: '2px 2px 5px 0px rgba(0,0,0,0.2)',
              borderRadius: '5px'
            }}>
            
              <div className="mb-4">{value.icon}</div>
              <h4
              className="mb-3"
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                color: '#122658'
              }}>
              
                {value.title}
              </h4>
              <p
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                color: '#7F7F90',
                lineHeight: 1.7
              }}>
              
                {value.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default AboutSection;